import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { BotanicalSpray } from "../components/Botanical";
import { scrollToHash } from "./Nav";

const HERO_IMG =
  "https://images.unsplash.com/photo-1785837623659-34c62ab510d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjZWxlYnJhdGlvbiUyMGNha2UlMjB3ZWRkaW5nJTIwY2FrZSUyMGFydGlzYW5hbCUyMGJha2VyeXxlbnwwfHx8fDE3OTExNTA2MDl8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["Sweet moments,", "beautifully", "baked."];

const ease = [0.22, 1, 0.36, 1];

const RotatingBadge = () => (
  <div className="absolute -bottom-8 -left-8 w-32 h-32 md:w-40 md:h-40 hidden sm:block" data-testid="hero-badge">
    <div className="absolute inset-0 rounded-full bg-[#F9F6F0] border border-[#C5A059]/50 shadow-xl" />
    <svg viewBox="0 0 100 100" className="absolute inset-0 animate-spin-slow">
      <defs>
        <path id="badge-circle" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
      </defs>
      <text className="fill-[#8B5D6C]" style={{ fontSize: "8.2px", letterSpacing: "2.6px", fontFamily: "Plus Jakarta Sans" }}>
        <textPath href="#badge-circle">SWEET MOMENTS • BEAUTIFULLY BAKED •</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <Sparkles size={22} className="text-[#C5A059]" />
    </div>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1.6, delay: 0.4 }}
        className="absolute -top-6 -left-10 w-56 md:w-80 text-[#C5A059]/50 pointer-events-none"
      >
        <BotanicalSpray flip />
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1.6, delay: 0.6 }}
        className="absolute bottom-0 right-0 w-64 md:w-96 text-[#B87D8E]/40 pointer-events-none rotate-180"
      >
        <BotanicalSpray />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center relative z-10 w-full">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="gold-rule w-14" />
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#8B5D6C]" data-testid="hero-eyebrow">
              Velvet Bakes · Bespoke Celebration Cakes
            </p>
          </motion.div>

          <h1 className="font-serif font-light tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-[5.4rem] text-[#4A2834]" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic text-[#B87D8E]" : ""} ${i === 2 ? "italic text-[#8B5D6C]" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.35 + i * 0.16, ease }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease }}
            className="mt-8 max-w-xl text-base md:text-lg text-[#70505D] leading-relaxed"
            data-testid="hero-subheadline"
          >
            Handcrafted celebration cakes, wedding tiers and patisserie by Lall Bansal.
            Every layer made to order, every sugar flower piped by hand, every moment
            made a little sweeter.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={() => scrollToHash("#order")}
              data-testid="hero-order-button"
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#8B5D6C] text-[#F9F6F0] text-sm font-medium tracking-wide border border-[#C5A059]/60 hover:bg-[#4A2834] transition-colors duration-300"
            >
              Request an Order
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToHash("#gallery")}
              data-testid="hero-gallery-button"
              className="text-sm font-medium tracking-wide text-[#8B5D6C] border-b border-[#C5A059] pb-1 hover:text-[#4A2834] transition-colors duration-300"
            >
              Explore the Gallery
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-12 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-[#987885]"
          >
            <span>Weddings</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>Birthdays</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>Cupcakes</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>Dessert Tables</span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease }}
            className="relative"
          >
            <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-t-[999px] border border-[#C5A059]/60" />
            <div className="relative rounded-t-[999px] overflow-hidden shadow-2xl shadow-[#8B5D6C]/25 aspect-[3/4]" data-testid="hero-image-frame">
              <motion.img
                src={HERO_IMG}
                alt="Three-tiered Velvet Bakes celebration cake with figs and blackberries"
                style={{ y: imgY, scale: 1.18 }}
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4A2834]/25 via-transparent to-transparent" />
            </div>
            <RotatingBadge />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
